/* eslint-disable react-hooks/error-boundaries */
import type { JSX, MouseEvent } from 'react';
import { useCallback } from 'react';

import omit from 'lodash/omit.js';
import type { LinkProps as BaseLinkProps } from 'react-router';
// eslint-disable-next-line no-restricted-imports
import { Link as BaseLink, useHref } from 'react-router';

import { isExternalHttpUrl } from '@change-corgi/core/navigation';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useNavigateExternal } from '../hooks/useNavigateExternal';
import { shouldNavigate } from '../shared/link-click';
import { getLinkRel } from '../shared/rel';

// remove non-html attributes from LinkProps and RouteComponentProps
const PROPS_TO_OMIT = [
	'theme',
	'location',
	'match',
	'replace',
	'state',
	'href',
	'children',
	'to',
	'preventScrollReset',
];

type ExternalProps = Readonly<{
	externalUrl?: string;
}>;

function ExternalLink({
	externalUrl,
	onClick: onClickInitial,
	ref,
	...props
}: BaseLinkProps & ExternalProps & { ref?: React.Ref<HTMLAnchorElement> }): JSX.Element | null {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const { replace, state, children, ...rest } = props;
	const navigateExternal = useNavigateExternal();

	const onClick = useCallback(
		(event: MouseEvent<HTMLAnchorElement>) => {
			onClickInitial?.(event);
			if (externalUrl && shouldNavigate(event)) {
				event.preventDefault();
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				navigateExternal(externalUrl, { replace: !!replace, state });
			}
		},
		[onClickInitial, navigateExternal, externalUrl, replace, state],
	);

	return (
		<a href={externalUrl} onClick={onClick} ref={ref} {...omit(rest, PROPS_TO_OMIT)}>
			{children}
		</a>
	);
}

export type LinkProps = BaseLinkProps &
	Readonly<{
		/**
		 * forces the navigation to be external/internal, ignoring the list of internal routes from the utility context
		 */
		forceMode?: 'internal' | 'external';
	}>;

/**
 * Wrapper of react-router's Link to handle external navigation
 */
export function Link({
	forceMode,
	ref,
	...props
}: LinkProps & { ref?: React.Ref<HTMLAnchorElement> }): JSX.Element | null {
	const { to } = props;

	const resolvedHref = useHref(to);
	const { errorReporter, navigation } = useUtilityContext();
	const href = typeof to === 'string' && isExternalHttpUrl(to) ? to : resolvedHref;

	try {
		const externalUrl = navigation.getExternalUrl(href, { forceMode });

		const { children, ...rest } = props;

		if (externalUrl) {
			return (
				<ExternalLink externalUrl={externalUrl} rel={getLinkRel(props)} ref={ref} {...rest}>
					{children}
				</ExternalLink>
			);
		}

		return (
			<BaseLink rel={getLinkRel(props)} ref={ref} {...rest}>
				{children}
			</BaseLink>
		);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		const { children, ...rest } = props;

		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		void errorReporter.report({ error: e, params: { target: to, component: 'Link' } });

		return (
			<ExternalLink rel={getLinkRel(props)} ref={ref} {...rest}>
				{children}
			</ExternalLink>
		);
	}
}
